/**
 * Type definitions for Spotlight Preview (Step 2) structures
 * Replaces loose 'any' types with proper TypeScript interfaces
 */

// Detailed interview data collected during onboarding
export interface DetailedInterview {
  before_moment?: string
  turning_point?: string
  approach_one_liner?: string
  first_call_expectations?: string
  testimonials?: string | string[]  // Can be array from legacy/context
  exclusions?: string | string[]  // Can be array from legacy/context
  origin_story?: string  // Practitioner's personal origin story ("why I do this work")
}

// Carousel customization settings
export interface CarouselCustomization {
  photo_focal_x: number
  photo_focal_y: number
  photo_scale: number
  photo_fit_mode: 'cover' | 'contain'
  photo_gradient_side: 'left' | 'right'
  photo_reveal: number
  identity_text_layout: 'side_strap' | 'bottom_banner' | 'transparent'
  photo_mode: 'original' | 'cutout'
  photo_shadow: boolean
  carousel_bg_palette?: string
}

// Carousel slide types
export type CarouselSlideType =
  | 'identity'
  | 'before'
  | 'after'
  | 'cta'
  | 'proof_or_filter'
  | 'struggle'
  | 'method'

export interface CarouselSlide {
  type: CarouselSlideType
  headline: string
  body: string
  image_url?: string
  cta_text?: string
  cta_url?: string
}

export interface CarouselCandidate {
  template_name: string
  slides: CarouselSlide[]
  template_id?: string
}

// Refinement action types
export type RefinementActionId =
  | 'before_moment'
  | 'turning_point'
  | 'approach_one_liner'
  | 'first_call_expectations'
  | 'testimonial'
  | 'exclusions'
  | 'origin_story'

export interface RefinementAction {
  id: RefinementActionId
  title: string
  description: string
  potentialIncrease: number
}

// Helper type for basic info with preview-specific fields
export interface BasicInfoWithPreview {
  name?: string
  photo_url?: string
  photo_cutout_url?: string
  photo_storage_path?: string
  who_you_help?: string
  offer_primary_outcome?: string
  specialty?: string
  photo_focal_x?: number
  photo_focal_y?: number
  photo_scale?: number
  photo_fit_mode?: 'cover' | 'contain'
  photo_gradient_side?: 'left' | 'right'
  photo_reveal?: number
  identity_text_layout?: 'side_strap' | 'bottom_banner' | 'transparent'
  photo_mode?: 'original' | 'cutout'
  photo_shadow?: boolean
  carousel_bg_palette?: string
}

// Generated assets structure
export interface GeneratedAssets {
  carousel_candidates?: CarouselCandidate[]
  landing_page?: any // TODO: Use LandingPageDraft from landing-page.ts
}

// Character limits for refinement fields
export const REFINEMENT_CHAR_LIMITS: Record<RefinementActionId, number> = {
  approach_one_liner: 140,
  before_moment: 300,
  turning_point: 300,
  first_call_expectations: 300,
  testimonial: 500,
  exclusions: 200,
  origin_story: 300
}

// Helper to safely get string from string | string[]
const getStringValue = (val: string | string[] | undefined): string => {
  if (!val) return ''
  if (Array.isArray(val)) return val[0] || ''
  return val
}

// Helper function to get field value from detailed interview
export const getRefinementFieldValue = (
  interview: DetailedInterview,
  actionId: RefinementActionId
): string => {
  switch (actionId) {
    case 'before_moment':
      return interview.before_moment || ''
    case 'turning_point':
      return interview.turning_point || ''
    case 'approach_one_liner':
      return interview.approach_one_liner || ''
    case 'first_call_expectations':
      return interview.first_call_expectations || ''
    case 'testimonial':
      return getStringValue(interview.testimonials)
    case 'exclusions':
      return getStringValue(interview.exclusions)
    case 'origin_story':
      return interview.origin_story || ''
    default:
      return ''
  }
}

// Helper function to create update object for detailed interview
export const createRefinementUpdate = (
  actionId: RefinementActionId,
  value: string
): Partial<DetailedInterview> => {
  if (actionId === 'testimonial') {
    return { testimonials: [value] }
  }
  if (actionId === 'exclusions') {
    return { exclusions: [value] }
  }
  // For others
  return { [actionId]: value }
}

// Type guard to check if value exceeds character limit
export const isOverCharLimit = (
  value: string,
  actionId: RefinementActionId
): boolean => {
  const limit = REFINEMENT_CHAR_LIMITS[actionId]
  return value.length > limit
}
